import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Wallet, AlertTriangle, Briefcase, Clock, FileText, CreditCard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function overdueClass(d) {
  if (d <= 30) return "bg-yellow-100 text-yellow-800 border-yellow-300";
  if (d <= 60) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-red-100 text-red-800 border-red-300";
}

export default function CustomerInsights({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/customers/${customerId}/summary`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="mt-8 text-sm text-muted-foreground">Loading insights…</div>;
  if (!data) return null;
  const s = data.stats;
  const ccy = s.currency || "EUR";
  const since = fmtDate(s.first_booking_at);
  const lastBooked = fmtDate(s.last_booking_at);

  return (
    <section className="mt-10 space-y-6" data-testid="customer-insights">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Insights</div>
        <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-1">Customer 360</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {s.first_booking_at
            ? <>Customer since <span className="font-semibold text-foreground">{since}</span>, last booked <span className="font-semibold text-foreground">{lastBooked}</span>.</>
            : "No bookings yet — KPIs will populate after the first booking."}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 -mx-2 px-2 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible">
        <Kpi testid="customer-kpi-revenue" icon={Wallet} label="Lifetime revenue"
             value={formatCurrency(s.lifetime_revenue_paid, ccy)}
             tone="emerald" />
        <Kpi testid="customer-kpi-outstanding" icon={AlertTriangle} label="Outstanding"
             value={formatCurrency(s.outstanding_total, ccy)}
             sub={s.overdue_total > 0 ? `${formatCurrency(s.overdue_total, ccy)} overdue` : null}
             tone={s.overdue_total > 0 ? "rose" : s.outstanding_total > 0 ? "amber" : "slate"} />
        <Kpi testid="customer-kpi-bookings" icon={Briefcase} label="Bookings"
             value={s.bookings_total}
             sub={`${s.bookings_completed} completed · ${s.bookings_upcoming} upcoming`} />
        <Kpi testid="customer-kpi-days-to-pay" icon={Clock} label="Avg days to pay"
             value={s.avg_days_to_pay !== null && s.avg_days_to_pay !== undefined ? Math.round(s.avg_days_to_pay) : "—"}
             sub={s.longest_days_to_pay != null ? `longest ${s.longest_days_to_pay}d` : null} />
        <Kpi testid="customer-kpi-avg-invoice" icon={FileText} label="Avg invoice"
             value={formatCurrency(s.avg_invoice_total, ccy)} />
        <Kpi testid="customer-kpi-preferred" icon={CreditCard} label="Preferred payment"
             value={s.preferred_payment_type?.name || "—"}
             sub={s.preferred_paid_method?.name ? `via ${s.preferred_paid_method.name}` : null} />
      </div>

      {/* Monthly revenue mini chart */}
      <div className="bg-card border rounded-[8px] p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Last 12 months · paid revenue</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={data.monthly_revenue} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={(m) => m.slice(2)} fontSize={10} />
            <YAxis tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : `${v}`} fontSize={10} width={36} />
            <Tooltip formatter={(v) => formatCurrency(v, ccy)} labelFormatter={(m) => m} />
            <Bar dataKey="revenue" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent bookings */}
      <Collapsible title="Recent bookings" testid="customer-recent-bookings" defaultOpen>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Booking</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Service</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_bookings.map((b) => (
                <tr key={b.booking_id} className="border-t">
                  <td className="px-3 py-2 mono text-xs">
                    <Link to={`/jobs/${b.booking_id}`} className="text-accent hover:underline">{b.booking_code}</Link>
                  </td>
                  <td className="px-3 py-2 mono text-xs">{(b.scheduled_start || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">{b.template_name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] mono uppercase border px-1.5 py-0.5 rounded">{b.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right mono">{formatCurrency(b.total, ccy)}</td>
                </tr>
              ))}
              {data.recent_bookings.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No bookings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Collapsible>

      {/* Outstanding invoices */}
      {data.outstanding_invoices.length > 0 && (
        <Collapsible title={`Outstanding invoices · ${data.outstanding_invoices.length}`} testid="customer-outstanding-invoices" defaultOpen>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Invoice #</th>
                  <th className="text-left px-3 py-2">Issued</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Aged</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.outstanding_invoices.map((i) => (
                  <tr key={i.invoice_id} className="border-t">
                    <td className="px-3 py-2 mono text-xs">{i.invoice_number}</td>
                    <td className="px-3 py-2 mono text-xs">{(i.issued_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] mono uppercase border px-1.5 py-0.5 rounded">{i.payment_status}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] mono uppercase border px-1.5 py-0.5 rounded ${overdueClass(i.days_overdue)}`}>{i.days_overdue}d</span>
                    </td>
                    <td className="px-3 py-2 text-right mono">{formatCurrency(i.total, ccy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}
    </section>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const toneCls = {
    emerald: "border-emerald-300 bg-emerald-50/40",
    rose:    "border-rose-300 bg-rose-50/40",
    amber:   "border-amber-300 bg-amber-50/40",
    slate:   "border-stone-200 bg-card",
  }[tone] || "border-stone-200 bg-card";
  return (
    <div className={`rounded-[8px] border ${toneCls} p-3 min-w-[160px]`} data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <div className="mono text-[10px] uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-xl md:text-2xl font-extrabold mt-1 truncate" title={typeof value === "string" ? value : ""}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Collapsible({ title, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-card border rounded-[8px]" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="font-bold">{title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t">{children}</div>}
    </div>
  );
}
